export type VehicleType =
  | 'BICYCLE'
  | 'ELECTRIC_BIKE'
  | 'ELECTRIC_SCOOTER'
  | 'MOTORCYCLE'
  | 'CAR';

export type YearsOfExperienceRange =
  | 'Y_1'
  | 'Y_2'
  | 'Y_3'
  | 'Y_4'
  | 'Y_5'
  | 'Y_6'
  | 'Y_7'
  | 'Y_8'
  | 'Y_9'
  | 'Y_10_PLUS';

/**
 * Fallback list used when the backend `GET /courier-onboarding/spoken-languages`
 * endpoint (which proxies the Monday `Languages Spoken` dropdown) is
 * unreachable. The authoritative list is what Monday returns at runtime, so
 * `SpokenLanguage` is intentionally widened to a plain string.
 */
export const SPOKEN_LANGUAGES = [
  'Hebrew',
  'Arabic',
  'English',
  'Russian',
] as const;
export type SpokenLanguage = string;

export type CourierDocumentKey =
  | 'profilePicture'
  | 'driverLicense'
  | 'businessLicense'
  | 'bankAccount';

export const PROFILE_PHOTO_REASONS = [
  'glasses',
  'helmet',
  'hat',
  'too_far',
  'no_face',
  'multiple_faces',
  'blurry',
  'too_dark',
  'not_facing_camera',
  'face_covered',
  'check_unavailable',
] as const;
export type ProfilePhotoReason = (typeof PROFILE_PHOTO_REASONS)[number];
export type ProfileCheckStatus = 'accepted' | 'rejected';

export type BusinessTrack = 'FINANCIAL_COMPANY' | 'LICENSED_BUSINESS';

export type CourierAreaType = 'EXPANSION' | 'OPERATION';

export type StepKey =
  | 'welcome'
  | 'intro'
  | 'personal'
  | 'documents'
  | 'review'
  | 'success';

/**
 * Steps that existed before the wizard was reduced to documents / personal /
 * review. Drafts autosaved on those steps still carry the old key, so they are
 * resumed on the merged data step instead of dropping the courier to the start.
 */
const RETIRED_STEP_KEYS = ['personalDetails', 'bankAccount'] as const;

export const normalizeStepKey = (
  step: string | undefined,
): StepKey | undefined => {
  if (!step) return undefined;
  if ((RETIRED_STEP_KEYS as readonly string[]).includes(step)) return 'personal';
  return step as StepKey;
};

export const requiredDocsByTrack: Record<BusinessTrack, CourierDocumentKey[]> = {
  FINANCIAL_COMPANY: ['profilePicture', 'driverLicense'],
  LICENSED_BUSINESS: [
    'profilePicture',
    'driverLicense',
    'bankAccount',
    'businessLicense',
  ],
};

export interface CourierDocument {
  key: CourierDocumentKey;
  name: string;
  type: string;
  size: number;
  url?: string;
  ocrStatus?: 'success' | 'failed';
  /** Set only for profilePicture, after the face check finishes. */
  profileCheckStatus?: ProfileCheckStatus;
  profileRejectionReasons?: ProfilePhotoReason[];
}

export const isRequiredDocumentReady = (
  key: CourierDocumentKey,
  documents: CourierDocument[],
): boolean => {
  const document = documents.find((item) => item.key === key && !!item.url);
  if (!document) return false;
  if (key === 'profilePicture') {
    return document.profileCheckStatus === 'accepted';
  }
  return true;
};

export interface CourierPersonalInfo {
  fullName: string;
  phoneNumber: string;
  email: string;
  taxId: string;
  dateOfBirth: string;
  city: string;
  spokenLanguages: SpokenLanguage[];
}

export interface CourierVehicleInfo {
  vehicleTypes: VehicleType[];
}

export interface CourierExperienceInfo {
  hasDeliveryExperience: boolean | null;
  yearsOfExperienceRange: YearsOfExperienceRange | null;
}

export interface CourierBankAccountInfo {
  businessName: string;
  bankCode: string;
  bankName: string;
  bankBranchNumber: string;
  bankAccountNumber: string;
  bankAccountOwnerName: string;
  bankAccountOwnerId: string;
}

/**
 * OCR extraction metadata surfaced per document slot. Persisted in shared
 * state (not just the documents step) so the cross-document ID mismatch and
 * age guards survive navigating back to the documents step from Review.
 */
export interface CourierOcrExtraction {
  extractedIdNumber?: string;
  birthDate?: string | null;
}

export type CourierOcrExtractions = Partial<
  Record<CourierDocumentKey, CourierOcrExtraction>
>;

export interface CourierOnboardingState {
  personalInfo: CourierPersonalInfo;
  vehicleInfo: CourierVehicleInfo;
  experienceInfo: CourierExperienceInfo;
  bankAccountInfo: CourierBankAccountInfo;
  documents: CourierDocument[];
  ocrExtractions: CourierOcrExtractions;
  notes: string;
  metadata: {
    documentsFirst: boolean;
    businessTrack: BusinessTrack | null;
    /** Area category of the selected city; drives available tracks. */
    areaType?: CourierAreaType;
    leadId?: string;
    resumeToken?: string;
    submissionId?: string;
    language?: 'en' | 'ar' | 'he';
    currentStep?: StepKey;
    maxReachedTracked?: number;
    phoneVerified?: boolean;
    verifiedPhone?: string;
    phoneVerificationToken?: string;
    otpChallengeId?: string;
    otpSentTo?: string;
  };
}

export const VEHICLE_TYPES: VehicleType[] = [
  'BICYCLE',
  'ELECTRIC_BIKE',
  'ELECTRIC_SCOOTER',
  'MOTORCYCLE',
  'CAR',
];

export const YEARS_RANGES: YearsOfExperienceRange[] = [
  'Y_1',
  'Y_2',
  'Y_3',
  'Y_4',
  'Y_5',
  'Y_6',
  'Y_7',
  'Y_8',
  'Y_9',
  'Y_10_PLUS',
];
